import { useCallback, useEffect, useReducer, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Fairness, MatchStatus } from '../api/types';
import { ChoiceButtons } from '../components/ChoiceButtons';
import { Countdown } from '../components/Countdown';
import { FairnessPanel } from '../components/FairnessPanel';
import { HistoryTable } from '../components/HistoryTable';
import { ReconnectBanner } from '../components/ReconnectBanner';
import { RevealPanel } from '../components/RevealPanel';
import { ScoreBoard } from '../components/ScoreBoard';
import { WaitingForOpponent } from '../components/WaitingForOpponent';
import { useI18n } from '../i18n';
import type { StringKey } from '../i18n/strings';
import { canSubmit, matchResultFor, opponentSeat, type MatchResult } from '../match/machine';
import { rematchErrorCode, useMatch } from '../match/useMatch';

/** `matchId` 為 null（不該發生，但 route 參數在型別上是 optional）時的 key。 */
const NO_MATCH_KEY = '__no_match__';

/**
 * ★ **一場對局 = 一份狀態**（F-5 的修法）。
 *
 * `useMatch()` 裡的 `useReducer(matchReducer, matchId, initialState)`，第三個參數是
 * **惰性初始化 —— 只在元件 mount 時執行一次**。再戰導向 `/m/<新 id>` 走的是同一條
 * route，React 會**重用**同一個元件實例（不 unmount），於是整份 state 原封不動被帶進
 * 新對局：舊種子、舊 history、舊比分、以及上一場出的那一手（`myPendingAction`）。
 *
 * 線上後果是公平面板端出「**新**對局的承諾 ＋ **舊**對局的種子」——
 * 一份平台自己偽造出來的作弊證據。這比顯示錯誤的比分嚴重得多。
 *
 * 修法是把「持有那份 state 的元件」用 `key` 綁在 `matchId` 上。React 的協調規則是
 * (type, key)：type 相同但 key 不同 ⇒ 舊實例 unmount、新實例 mount ⇒ 惰性初始化
 * 重跑 ⇒ 狀態自然歸零。這讓「一場對局 = 一份狀態」變成**結構上的保證**，
 * 而不是一條必須被記得維護的重置路徑。
 *
 * ★ key 必須加在**持有 `useMatch()` 的那一層**。
 *   * 加在 `<ScoreBoard>` / `<FairnessPanel>` 之類的子元件上**完全沒有效果** ——
 *     reducer 不住在它們裡面。
 *   * 也不能寫在 `App.tsx` 的 `<Route element={<MatchPage />}>`，那一層還在
 *     `useParams()` 之前，拿不到 `matchId`。
 *   所以才有 `MatchPage`（讀 route 參數）/ `MatchPageInner`（持有狀態）這薄薄兩層。
 *
 * 抽成一支獨立函式是為了讓這條規則**可被測試**：測試可以直接比對兩個 matchId 產生的
 * (type, key)，證明 React 會重建而不是重用（見 `matchPage.test.ts`）。
 */
export function matchPageElement(matchId: string | null) {
  return <MatchPageInner key={matchId ?? NO_MATCH_KEY} matchId={matchId} />;
}

export function MatchPage() {
  const { matchId = null } = useParams();
  return matchPageElement(matchId);
}

/**
 * ★ C01 §4.2 的承諾-揭露順序：`server_seed` 只有在對局 `completed` 之後才可以進入畫面。
 *
 * 為什麼在這裡切掉，而不是「反正那時候狀態剛好是空的」：
 *  * 「剛好是空的」是巧合不是保證 —— F-5 就是這個巧合失效的實例。
 *  * 判斷用 `status`、**不用** `ended`：`ended` 對 `voided` 也是 true，而 voided /
 *    abandoned 的對局**永遠不會**有種子（後端 `match_service.view_for()` 的守衛就是
 *    `match.status == "completed"`）。用 `ended` 當條件，正好漏掉唯一一條會讓玩家
 *    真的按出 ❌ 的路徑：新對局以 `match_voided` 收場、面板卻還握著上一場的種子。
 *  * 面板上「（尚未公布）」那一列是刻意的教學設計（見 `FairnessPanel` 檔頭），
 *    所以這裡不是把整列拿掉，而是保證終局前那一列**沒有值可顯示** ——
 *    連驗證按鈕都不會出現（面板的條件是 `ended && fairness.server_seed`）。
 *
 * 沒東西要遮時回傳原物件，維持 referential equality（面板的 `useCallback` 依賴它）。
 */
export function fairnessForDisplay(fairness: Fairness, status: MatchStatus | null): Fairness {
  if (status === 'completed') return fairness;
  if (fairness.server_seed === null) return fairness;
  return { ...fairness, server_seed: null };
}

/**
 * ★ 再戰導頁的單一決策點（F-3）。
 *
 * 新對局的 id 有兩條抵達路徑，而且是**不對稱**的：
 *  * **後按的人**：`POST /matches/{id}/rematch` 直接回 `{"status":"matched"}`；
 *  * **先按的人**：只有 WS 的 `rematch_ready` 事件 —— 他的 HTTP 回應在新對局被建立
 *    之前就已經結束了（C02 §5 v1.2）。沒有這個事件，他必定永遠停在「等待對手同意」。
 *
 * 後按的人**兩條都會收到**，而契約強制兩者的 `match_id` 一致。因此這裡以
 * 「已經導向過哪一個 id」為準：**同一個目標只導一次**，兩條路徑先到先算。
 */
export function shouldNavigateToRematch(
  currentMatchId: string | null,
  target: string | null,
  navigatedTo: string | null,
): boolean {
  if (target === null || target === '') return false;
  if (target === currentMatchId) return false; // 已經在新對局裡了
  if (target === navigatedTo) return false; // 另一條路徑已經導過
  return true;
}

// ---------------------------------------------------------------------------
// ★ 再戰的等待 —— 「等待」不是終局狀態，而「過期」不是前端可以自己宣布的事
//
//   第二輪修好了「等待沒有盡頭」（按鈕永久鎖死），但留下一個更難看見的東西：
//   **UI 自己算出一個放棄時點，而伺服器上那把鑰匙的死亡時刻是另一個數字。**
//   兩個數字各自都說得通，於是沒有人發現它們不相等 —— 這是本專案第四次出現
//   同一種缺陷（F-4 欄位名、A-1 期限單位、A-3 訂閱順序，然後是這裡的 R-5）。
//
//   本節的設計規則只有一條，其餘都是它的推論：
//   ★ **「再戰邀請已經過期」是一個關於伺服器狀態的事實宣稱。
//      前端只能從伺服器親口給過的答案推導它，不得自己挑一個秒數。**
// ---------------------------------------------------------------------------

/**
 * ★ 伺服器上那把「我要再戰」鑰匙的**壽命**（`api/routes_matches.py:42`
 * `REMATCH_TTL_S` = **120 秒**）。
 *
 * 注意這個常數的語意在這一輪被修正了：它**不是**「前端願意等多久」，
 * 而是「伺服器在**最後一次**收到我的 `POST /rematch` 之後，還會替我保留意願多久」。
 * 後端每一次呼叫都做：
 *
 * ```python
 * settled = await redis.get(key_new)      # 已成局 → 零副作用早退
 * if settled: return {"status": "matched", ...}
 * await redis.set(key_mine, "1", ex=REMATCH_TTL_S)   # ← ★ 每一次都**重新**續期
 * ```
 *
 * 也就是說鑰匙的死亡時刻是 **`最後一次請求 + 120s`**，不是 `按下 + 120s`。
 * 第二輪把常數對齊成 120，卻把**原點**錨在按下的那一刻，於是：
 *
 * | 量 | 值 |
 * | :-- | :-- |
 * | UI 宣布過期 | 120,000 ms |
 * | 最後一次續期輪詢 | 115,000 ms |
 * | 伺服器實際忘記我 | 235,000 ms |
 * | **盲窗** | **115,000 ms** |
 *
 * 那 115 秒裡，UI 說「過期了」、伺服器說「還在等」。對手只要在這段時間按下再戰，
 * 後端會**真的**開出新對局並把我放進一個座位，而我的畫面已經停止輪詢 ——
 * 30 秒後 `abandon_no_show()` 就讓我對著一個在線的對手被判負
 * （`match_ready_timeout_s` = 30，見下方 `MATCH_READY_TIMEOUT_MS`）。
 *
 * 修法不是「把 120 改成 235」（那只是換一個一樣會漂移的常數），而是
 * **讓放棄時點只有一個來源**：`intentExpiresAt` 只由「觀察到一次 POST 的結果」
 * 這條路徑寫入，而那正是伺服器做 `SET ... EX 120` 的同一次呼叫。
 * 一個事件、一個原點、一個數字 —— 沒有第二個數字可以跟它漂移。
 */
export const REMATCH_TTL_MS = 120 * 1000;

/**
 * ★ 後端 `match_ready_timeout_s`（`config/settings.py:123` = **30 秒**）。
 *
 * 這裡用不到它來排程，它是一個**上界**：新對局一被建立就有
 * `ready_deadline_at = now + 30s`，時間到而我還沒連上，reaper 會呼叫
 * `abandon_no_show()`；此時對手已經連上（他才剛按下再戰），於是
 * 「恰好一位連上」那一支會把**我**判成 forfeit。
 *
 * 所以「發現新對局」的延遲不是體驗問題，是**輸贏問題**。任何守望的週期
 * 都必須明顯小於它，並由測試釘住這個不等式。
 */
export const MATCH_READY_TIMEOUT_MS = 30 * 1000;

/**
 * ★ 等待期間重打 `POST /rematch` 的週期。
 *
 * 這條輪詢是 `rematch_ready` 掉包時的**補救路徑**，不是主通道（主通道仍是事件）。
 * 它安全的理由見 `useMatch.ts` 的 `rematch()` 註解：後端的早退發生在寫入之前，
 * 重打是零副作用的讀取。
 *
 * 5 秒的理由（三個上界一起夾出來的）：
 *  1. **補救延遲**：事件掉包時玩家最多多等一個週期。5 秒在結束畫面上還在「沒感覺」的範圍，
 *     30 秒就已經是「這東西壞了」。
 *  2. **速率預算**：這支端點吃 `lobby_queue` 這個 bucket（`routes_matches.py:235`），
 *     per-subject **30 次/分鐘**（C02 §10）。5 秒 = 12 次/分鐘，只用掉四成，
 *     剩下的留給玩家同時在大廳做別的事。
 *  3. **重連視窗**：WS 退避最長 15 秒，期間 pub/sub 沒有回溯。週期必須明顯小於它，
 *     否則整個重連視窗可能一次輪詢都沒發生。
 */
export const REMATCH_POLL_INTERVAL_MS = 5 * 1000;

/**
 * ★ 前端願意**主動續期**多久（＝按鈕變灰、每 5 秒重打一次的那一段）。
 *
 * 這是本節唯一一個「由前端決定」的數字，而且它決定的**不是**過期時刻，
 * 只是「我還要不要繼續把伺服器上那把鑰匙往後推」。取 `REMATCH_TTL_MS` 的理由：
 *
 *  * 後端自己認為一份再戰意願值得保留 120 秒（`REMATCH_TTL_S` 的註解寫得很清楚：
 *    「沒有 TTL 的話，一個小時前按過再戰的人會在對方按下去的瞬間被拉進一場
 *    他早就離開的對局」）。前端持續續期，等於單方面推翻那個判斷。
 *  * 玩家看到的「按鈕變灰」時間因此仍然是 2 分鐘，與第二輪一致 —— 這一輪改的是
 *    **之後發生什麼事**，不是把玩家多綁 2 分鐘。
 *
 * ⚠️ 停止續期**不等於**可以宣布過期。停止續期只是「我不再往後推」；
 *    鑰匙還會依照它自己的 TTL 再活最多 120 秒（見 `RematchUiState.watching`）。
 *    把這兩件事寫成同一個時刻，正是 R-5。
 */
export const REMATCH_ACTIVE_WINDOW_MS = REMATCH_TTL_MS;

/**
 * ★ 尾窗（已停止續期、鑰匙還沒死）的**唯讀**守望週期。
 *
 * 尾窗裡不能再打 `POST /rematch` —— 那會續期，鑰匙就永遠不死，
 * 等於把後端的 TTL 設計整個廢掉。但也**不能什麼都不做**：
 * 對手這時按下再戰，後端會真的開出新對局，而 `rematch_ready` 是會掉的
 * （publish 失敗只記 log 仍回 200；WS 退避重連期間 pub/sub 沒有回溯）。
 *
 * 出路是一條**對再戰意願零副作用**的路徑：`GET /lobby/{game_key}` 的
 * `my_active_match`（`lobby/service.py:144`）。它讀的是
 * `active_match_for_subject()`（status ∈ waiting/ready/in_progress，取最新一筆），
 * 而新開的再戰對局正是 `status='ready'`。整條路徑不碰任何 redis key。
 *
 * 10 秒的三個上界：
 *  1. **必須明顯小於 `MATCH_READY_TIMEOUT_MS`（30 秒）** —— 發現得太慢就等於被判負，
 *     還要留時間給導頁 + `GET /matches` + WS 訂閱。10 秒留下 20 秒餘裕。
 *  2. **速率**：走 `read` bucket（C02 §10「其他 GET」600 次/分鐘），6 次/分鐘是零頭。
 *  3. **不必更快**：主通道仍然是 `rematch_ready` 事件，這條只是備援的備援。
 */
export const REMATCH_WATCH_INTERVAL_MS = 10 * 1000;

/**
 * 再戰按鈕這一側的畫面狀態。
 *
 * 舊版是一個 `useState(false)` 的 `rematchSent`，而 `setRematchSent(false)`
 * **只在 `catch` 裡** —— HTTP 200 不會進 catch，所以按過一次就永遠不能再按。
 * 「已送出」被當成了終局狀態：沒有逾時、沒有重新啟用、也沒有任何說明。
 *
 * 這一輪再拆一次：**「按鈕還給玩家」與「伺服器忘了我」是兩件事**，
 * 中間隔著整整一個 TTL。第二輪把它們寫成同一個時刻，於是有了 R-5 的盲窗。
 */
export type RematchUiState =
  /** 還沒按，或上一輪已經收尾 —— 按鈕可按。 */
  | { kind: 'idle' }
  /**
   * 已送出、每 5 秒續期一次。**這是唯一會讓按鈕變灰的狀態。**
   *  * `intentExpiresAt`：伺服器最晚在這一刻忘記我。**只由觀察到的 POST 結果寫入。**
   *  * `renewUntil`：停止主動續期的時刻（前端的預算，不是事實宣稱）。
   *  * `nextPollAt`：下一次重打的時刻。
   */
  | { kind: 'waiting'; intentExpiresAt: number; renewUntil: number; nextPollAt: number }
  /**
   * ★ 尾窗：**按鈕已經還給玩家，但伺服器上的意願還活著。**
   *
   * 這個狀態就是 R-5 缺的那一塊。它同時保證兩件事：
   *  1. 畫面**不得**說「過期了」—— 那句話在這段時間裡是假的；
   *  2. 畫面**不得**停止尋找新對局 —— 對手這時按下我真的會被放進一個座位。
   *
   * `error` 非 null 時代表這一段是由一次「結果不明的失敗」進來的
   * （見 `mayHaveArmedIntent`）：訊息照樣要說出來，但監護責任不能因此丟掉。
   */
  | { kind: 'watching'; intentExpiresAt: number; nextWatchAt: number; error: { code: string; message: string } | null }
  /**
   * ★ 伺服器真的忘記我了（`now >= intentExpiresAt`）。**只有這個狀態可以說「過期」。**
   * 此後對手再怎麼按都只會拿到 `waiting_for_opponent`（成局要兩把鑰匙同時在），
   * 所以停止守望在這裡是安全的 —— 而且是**唯一**安全的地方。
   */
  | { kind: 'expired' }
  /** 伺服器明確拒絕，且**證明**它在寫入意願之前就拒絕了 —— 沒有東西要監護。 */
  | { kind: 'failed'; code: string; message: string };

export type RematchUiEvent =
  | { type: 'PRESSED'; now: number }
  /**
   * ★ 觀察到一次 `POST /rematch` 的結果，而那次呼叫（可能）已經在伺服器上
   * 做了 `SET key_mine EX 120`。`now` 取**收到回應**的時刻，不是送出的時刻 ——
   * 伺服器的 `SET` 一定發生在這兩者之間，所以用回應時刻推出來的
   * `now + TTL` 是伺服器真正死亡時刻的**上界**，前端因此不可能提早放棄。
   */
  | { type: 'INTENT_RENEWED'; now: number }
  /** 任一條路徑（HTTP `matched` 或 WS `rematch_ready`）拿到了新對局。 */
  | { type: 'MATCHED' }
  | { type: 'FAILED'; code: string; message: string; now: number }
  | { type: 'TICK'; now: number };

export const IDLE_REMATCH: RematchUiState = { kind: 'idle' };

/**
 * ★ **這個狀態底下，伺服器上還可能有一份屬於我的再戰意願活到什麼時候？**
 *
 * 整節的核心問句就是這一句，所以把它寫成一支函式而不是散在各處的 `if`。
 * `null` = 沒有任何東西要監護，可以安心收工。
 *
 * 「還可能」是刻意的：`NETWORK_ERROR` 之後我們**不知道**請求有沒有送達，
 * 而猜錯的兩個方向代價完全不對稱 —— 多守望一分鐘只是多幾個 GET，
 * 少守望一分鐘是玩家被判負。
 */
export function outstandingIntentExpiresAt(state: RematchUiState): number | null {
  if (state.kind === 'waiting' || state.kind === 'watching') return state.intentExpiresAt;
  return null;
}

/**
 * ★ 這個錯誤碼有沒有可能是在伺服器**已經寫下我的意願之後**才發生的？
 *
 * 逐條對照 `routes_matches.py::rematch()` 的 raise 點：
 * `enforce_rate_limit`（429）、`MATCH_NOT_FOUND`、`MATCH_NOT_ENDED`、
 * `REMATCH_DISABLED`、`NO_OPPONENT` —— **全部**在
 * `await container.redis.set(key_mine, …)` 之前，所以它們證明了「什麼都沒被寫下」。
 *
 * 只有兩種情況證明不了：
 *  * `NETWORK_ERROR`：請求可能已經送達，只是回應沒回來；
 *  * `HTTP_5xx`：`create_match()` 失敗的那條路徑就在 `set(key_mine)` **之後**。
 *
 * 證明不了就得當作有 —— 見 `outstandingIntentExpiresAt` 的不對稱代價。
 */
export function mayHaveArmedIntent(code: string): boolean {
  return code === 'NETWORK_ERROR' || /^HTTP_5\d\d$/.test(code);
}

/**
 * ★ 等待的狀態機。純函式，所以「等待有沒有出路」「有沒有盲窗」這兩件事都可以被**證明**，
 * 而不是靠人眼看 JSX。
 *
 * `TICK` 在「什麼都還不用做」時**回傳同一個物件**（referential equality），
 * 讓 React 直接略過重繪。代價是呼叫端必須保證：**每一個被排定的醒來時刻，
 * 都一定會讓狀態前進**（否則計時器就此停擺）。`nextRematchWakeupMs()` 只會排在
 * 四個邊界上（`nextPollAt` / `renewUntil` / `nextWatchAt` / `intentExpiresAt`），
 * 而這四個邊界在下面都各自對應一條會產生新物件的分支。
 */
export function rematchUiReducer(state: RematchUiState, ev: RematchUiEvent): RematchUiState {
  switch (ev.type) {
    case 'PRESSED':
      // 每一次按下都是一輪**全新**的等待：伺服器那一側也是重新 `SET ... EX 120`。
      // `intentExpiresAt` 這裡先給一個**下界**（按下的時刻 + TTL）；真正的值由
      // 第一個 `INTENT_RENEWED` 往後修正 —— 伺服器的 `SET` 一定發生在按下之後。
      return {
        kind: 'waiting',
        intentExpiresAt: ev.now + REMATCH_TTL_MS,
        renewUntil: ev.now + REMATCH_ACTIVE_WINDOW_MS,
        nextPollAt: ev.now + REMATCH_POLL_INTERVAL_MS,
      };

    case 'INTENT_RENEWED': {
      // 伺服器剛剛（可能）把鑰匙續到 `now + TTL`。只能往後推，不能往前縮 ——
      // 亂序抵達的回應不得把監護期限剪短。
      const at = Math.max(ev.now + REMATCH_TTL_MS, outstandingIntentExpiresAt(state) ?? -Infinity);
      if (state.kind === 'waiting') return { ...state, intentExpiresAt: at };
      if (state.kind === 'watching') return { ...state, intentExpiresAt: at };
      return state;
    }

    case 'MATCHED':
      // 導頁會讓整個元件連同這份狀態一起消失（`key={matchId}`）；
      // 萬一沒有（例如目標就是目前這場），回 idle 也比留在 waiting 好。
      return IDLE_REMATCH;

    case 'FAILED': {
      /*
       * ★ 失敗**不會**解除監護責任。
       *   「這次呼叫失敗了」與「伺服器上沒有我的意願」是兩件事：
       *   之前的輪詢可能早就把鑰匙armed起來了，而這一次的失敗（若是 5xx / 網路）
       *   自己也可能又armed了一次。丟掉監護 = 重新製造盲窗。
       */
      const carried = outstandingIntentExpiresAt(state);
      const armed = mayHaveArmedIntent(ev.code)
        ? Math.max(ev.now + REMATCH_TTL_MS, carried ?? -Infinity)
        : carried;
      if (armed === null) return { kind: 'failed', code: ev.code, message: ev.message };
      return {
        kind: 'watching',
        intentExpiresAt: armed,
        nextWatchAt: ev.now + REMATCH_WATCH_INTERVAL_MS,
        error: { code: ev.code, message: ev.message },
      };
    }

    case 'TICK': {
      if (state.kind === 'waiting') {
        // 順序不可換：先問「伺服器忘了我沒有」，再問「要不要停止續期」。
        // `intentExpiresAt` 是事實，`renewUntil` 只是預算。
        if (ev.now >= state.intentExpiresAt) return { kind: 'expired' };
        if (ev.now >= state.renewUntil) {
          return {
            kind: 'watching',
            intentExpiresAt: state.intentExpiresAt,
            nextWatchAt: ev.now + REMATCH_WATCH_INTERVAL_MS,
            error: null,
          };
        }
        if (ev.now >= state.nextPollAt) return { ...state, nextPollAt: ev.now + REMATCH_POLL_INTERVAL_MS };
        return state;
      }
      if (state.kind === 'watching') {
        if (ev.now >= state.intentExpiresAt) return { kind: 'expired' };
        if (ev.now >= state.nextWatchAt) return { ...state, nextWatchAt: ev.now + REMATCH_WATCH_INTERVAL_MS };
        return state;
      }
      return state;
    }

    default:
      return state;
  }
}

/**
 * 這一刻該不該重打 `POST /rematch`（＝**續期**）。
 *
 * 只在 `waiting` 且**還在續期預算內**時才打。到了 `renewUntil` 就換成唯讀守望 ——
 * 再打下去鑰匙會被無限續期，後端那條「不要把一小時前按過的人拉進來」的設計就沒了。
 */
export function shouldPollNow(state: RematchUiState, now: number): boolean {
  if (state.kind !== 'waiting') return false;
  if (now >= state.renewUntil) return false;
  return now >= state.nextPollAt;
}

/**
 * 這一刻該不該做一次**唯讀**守望（`GET /lobby/{game_key}` → `my_active_match`）。
 *
 * ★ **意願死亡的那一刻一定要看最後一眼**（`Math.min(...)` 的用意）。
 *   對手可以在 `intentExpiresAt` 的前一毫秒按下並成局；若守望只照自己的
 *   10 秒節奏走，那一場對局最多可能晚 10 秒才被發現 —— 而 `abandon_no_show()`
 *   只給 30 秒。把最後一眼釘在死亡時刻，尾窗就**沒有任何一段**是沒被看過的。
 *
 * 這最後一眼是安全的：它是 `GET`，不會續期；而且此時 key 已經死了，
 * 就算後端那條「已成局就零副作用早退」沒被走到也不會有副作用。
 */
export function shouldWatchNow(state: RematchUiState, now: number): boolean {
  if (state.kind !== 'watching') return false;
  return now >= Math.min(state.nextWatchAt, state.intentExpiresAt);
}

/**
 * 下一次該醒來的毫秒數；`null` = 真的沒事要做了。
 *
 * ★ **`null` 的意思是「伺服器已經忘記我」，不是「我不想等了」。**
 *   這支函式回 `null` 的那一刻，就是盲窗的起點；所以它只在
 *   `outstandingIntentExpiresAt() === null` 時才回 `null`。
 *   第二輪的版本在 `expired` 就回 `null`，而那時鑰匙還活著 115 秒 —— 那就是 R-5。
 */
export function nextRematchWakeupMs(state: RematchUiState, now: number): number | null {
  if (state.kind === 'waiting') {
    const at = Math.min(state.nextPollAt, state.renewUntil, state.intentExpiresAt);
    return Math.max(0, at - now);
  }
  if (state.kind === 'watching') {
    return Math.max(0, Math.min(state.nextWatchAt, state.intentExpiresAt) - now);
  }
  return null;
}

/**
 * ★ 輪詢途中的失敗要不要中止等待？
 *
 * 只有**伺服器明確拒絕**才中止。一次網路抖動、一次 429、一次
 * `MATCH_BUSY`（`_elect_rematch_match_id()` 在選舉退化時回的 409，語意就是「等一下再試」）
 * 都不該把玩家唯一的出路關掉 —— 反正等待本來就有 TTL 兜底。
 *
 * 反過來，`MATCH_NOT_ENDED` / `REMATCH_DISABLED` / `MATCH_NOT_FOUND` / `NO_OPPONENT`
 * 再打一百次也是同一個答案，繼續等只是浪費玩家的時間。
 */
const TRANSIENT_REMATCH_CODES = new Set(['NETWORK_ERROR', 'RATE_LIMITED', 'MATCH_BUSY', 'IDEMPOTENCY_IN_PROGRESS']);

export function isTransientRematchFailure(code: string): boolean {
  return TRANSIENT_REMATCH_CODES.has(code);
}

export type RematchAffordance =
  | { kind: 'button'; disabled: boolean; labelKey: StringKey }
  | { kind: 'unavailable'; reasonKey: StringKey };

/**
 * ★ 再戰按鈕該長什麼樣，或者根本不該出現。
 *
 * `phase === 'ended'` 對 `abandoned` 也成立，而後端的前置條件是
 * `if match.status != "completed": raise ApiError("MATCH_NOT_ENDED", ..., 409)`。
 * 也就是說：對一場 abandoned 的對局，那顆按鈕是**可按但必定失敗**的。
 * 一顆保證失敗的按鈕不是「有選項」，是誘導玩家反覆去撞同一面牆 ——
 * 所以這裡直接不給按鈕，改成一句說明。
 */
export function rematchAffordance(status: MatchStatus | null, ui: RematchUiState): RematchAffordance {
  if (status !== 'completed') return { kind: 'unavailable', reasonKey: 'match.rematch.unavailable' };
  if (ui.kind === 'waiting') return { kind: 'button', disabled: true, labelKey: 'match.rematch.waiting' };
  // ★ `watching` 的按鈕**是可按的**：續期預算用完了，主導權還給玩家 ——
  //   但背景的唯讀守望還在跑（見 `RematchUiState.watching`）。
  //   「把按鈕還給玩家」與「宣布過期」在這一輪之後是兩件事。
  return { kind: 'button', disabled: false, labelKey: 'match.rematch' };
}

/** `POST /rematch` 的失敗碼 → 文案。獨立出來是因為 `watching` 與 `failed` 兩個狀態都要用。 */
function rematchFailureKey(code: string): { key: StringKey; tone: 'warn' | 'error' } {
  switch (code) {
    case 'MATCH_NOT_ENDED':
      return { key: 'match.rematch.unavailable', tone: 'error' };
    case 'REMATCH_DISABLED':
      return { key: 'match.rematch.disabled', tone: 'error' };
    case 'MATCH_NOT_FOUND':
      return { key: 'error.notFound', tone: 'error' };
    case 'RATE_LIMITED':
      return { key: 'error.rateLimited', tone: 'warn' };
    case 'NETWORK_ERROR':
      return { key: 'error.network', tone: 'error' };
    default:
      return { key: 'match.rematch.failed', tone: 'error' };
  }
}

/**
 * 等待收場時要對玩家說的那一句話。`null` = 沒有話要說（還沒按 / 正在等）。
 *
 * ★ `watching` 與 `expired` 的差別就是 R-5 的全部內容：
 *   * `watching`：邀請**還在**伺服器上 —— 說「還沒回應」，**不能**說「過期」；
 *   * `expired`：伺服器真的忘了 —— 這時候才可以說「過期」。
 *   第二輪只有後者，於是那句「已經過期了」在盲窗的 115 秒裡是一句假話。
 */
export function rematchNotice(ui: RematchUiState): { key: StringKey; tone: 'warn' | 'error' } | null {
  if (ui.kind === 'expired') return { key: 'match.rematch.noResponse', tone: 'warn' };
  if (ui.kind === 'watching') {
    return ui.error ? rematchFailureKey(ui.error.code) : { key: 'match.rematch.stillOpen', tone: 'warn' };
  }
  if (ui.kind !== 'failed') return null;
  return rematchFailureKey(ui.code);
}

// ---------------------------------------------------------------------------
// ★ R-6：玩家看得到的字串一律要有中文文案；技術細節進 console，不要進畫面
// ---------------------------------------------------------------------------

/**
 * ★ `state.error` → 玩家看得懂的一句話。
 *
 * 缺陷：這裡本來只翻譯 `ROUND_MISMATCH`，其餘直接把 `state.error.message` 畫成紅色橫幅。
 * 那個 message 是**寫給開發者的英文技術字串**，例如 C02 §6.2 的期限退化：
 *
 * ```
 * round_started: deadline_at absent — derived from occurred_at + move_timeout_ms (C02 §6.2)
 * ```
 *
 * 玩家看到這句話只會得到兩個結論：這網站壞了、而且它不覺得我需要看懂。
 * 更糟的是 §6.2 的退化路徑是**刻意 fail-open** 的（倒數還在跑、對局完全正常），
 * 卻長得像系統崩潰。
 *
 * 規則：**畫面只出現字典裡的 key；`code` 與 `message` 一律進 console**
 * （由 `useMatch` 的副作用層負責，reducer 保持純函式）。
 * 認不得的碼落到 `error.generic` —— 一句無害的中文，而不是一段英文堆疊。
 */
export function errorNoticeKey(code: string): StringKey {
  switch (code) {
    case 'ROUND_MISMATCH':
      return 'error.roundMismatch';
    // C02 §6.1 / §6.2 的協定不相容。對玩家而言重點只有一個：
    // 「畫面上的資訊可能不完整，但你的對局沒事」。
    case 'PROTOCOL_MISMATCH':
    case 'REMATCH_EVENT_INVALID':
      return 'error.protocol';
    // R-2：對一場已經結束（或**還沒開始**，見稽核 I-2）的對局出手。
    case 'MATCH_ALREADY_ENDED':
    case 'MATCH_NOT_ENDED':
      return 'error.matchNotPlayable';
    case 'ALREADY_SUBMITTED':
      return 'error.alreadySubmitted';
    case 'ILLEGAL_ACTION':
      return 'error.illegalAction';
    case 'MATCH_NOT_FOUND':
      return 'error.notFound';
    case 'RATE_LIMITED':
      return 'error.rateLimited';
    case 'NETWORK_ERROR':
      return 'error.network';
    case 'IDEMPOTENCY_IN_PROGRESS':
    case 'IDEMPOTENCY_KEY_REUSE':
      return 'error.retrying';
    default:
      return 'error.generic';
  }
}

/**
 * ★ 管理員在對局中發出的公告（需求 1.2.3，C02 §7 的 `POST /matches/{id}/message`）。
 *
 * 事件那一半（`admin_action` → reducer，缺陷 R-3）由另一個 agent 完成，
 * 介面就是 orchestrator 指定的那一把：**`state.announcement: string | null`**
 * （`machine.ts` 另有 `announcements: MatchAnnouncement[]` 保留完整順序，
 * 想做「公告歷程」時讀它；這裡只顯示最新一則）。
 *
 * 仍然用執行期檢查而不是直接讀型別欄位，理由與 `readRematchMatchId()` 完全相同：
 * 兩邊各自綠燈但接縫錯掉是本專案反覆出現的失效模式，所以讀的那一側**再驗一次**。
 * 附帶好處是這支函式不必等 reducer 那一半就能編譯與測試。
 */
export function announcementText(state: { announcement?: unknown }): string | null {
  const v: unknown = state.announcement;
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  return trimmed === '' ? null : trimmed;
}

// ---------------------------------------------------------------------------
// 結束畫面的標題
// ---------------------------------------------------------------------------

/**
 * ★ `Record<MatchResult, StringKey>` 少寫一支就編譯失敗 ——
 *   「讀不到勝者」因此**不可能**再安靜地退化成「和局」（C02 §6.1 v1.2）。
 *   這條保證是 F-4 的修復留下來的，本次不得因為多了 `abandoned` 分支而弄丟。
 */
const RESULT_HEADLINE: Record<MatchResult, StringKey> = {
  win: 'match.won',
  lose: 'match.lost',
  draw: 'match.drawn',
  unknown: 'match.resultUnknown',
};

/**
 * ★ 結束畫面的標題。**先看 `status`，再看勝負。**
 *
 * `machine.ts` 的 `hydrateOutcome()` 只在 `status === 'completed'` 時填結果
 * （那是對的：`abandoned` / `voided` 本來就可能沒有勝者，硬要求 `winner_seat`
 * 會製造假警報）。代價是 `abandoned` 落到 `finalOutcome = null` → `matchResultFor()`
 * 回 `'unknown'` → 標題「結果未知」。
 *
 * 但這兩件事對玩家的意義完全不同：
 *  * **結果未知** = 「這個前端讀不懂伺服器」，暗示他去回報一個 bug；
 *  * **對手沒有出現** = 一件他看得懂、而且真的發生了的事。
 *
 * 這條路徑會被真人踩到：F-1 記載邀請碼建立的對局受 30 秒 ready 逾時管轄，
 * 朋友還沒點開連結那場對局就 `abandoned` 了。
 *
 * 分辨放在**這一層**而不是 `machine.ts`：reducer 的三態是「勝者讀不讀得到」，
 * 那是協定層的事實，不該因為畫面想講一句更好的話而被改寫。
 */
export function endedHeadlineKey(status: MatchStatus | null, result: MatchResult): StringKey {
  if (status === 'abandoned') return 'match.abandoned';
  if (status === 'voided') return 'match.voided'; // phase='voided' 通常先攔下來，這裡是保險
  return RESULT_HEADLINE[result];
}

/** 標題底下的補充說明。`null` = 標題已經說完了。 */
export function endedHintKey(status: MatchStatus | null, result: MatchResult): StringKey | null {
  if (status === 'abandoned') return 'match.abandoned.hint';
  if (status === 'voided') return null;
  return result === 'unknown' ? 'match.resultUnknown.hint' : null;
}

/**
 * ★ 對戰畫面。
 *
 * 這個檔案刻意很薄：所有「現在該畫什麼」的判斷都來自 machine.ts 的 phase 與 selector。
 * 新增畫面狀態時，請先改 machine + 補測試，再回來改這裡。
 */
function MatchPageInner({ matchId }: { matchId: string | null }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { state, rematchMatchId, remainingMs, progress, submit, rematch, findMyActiveMatch, forfeit, loadError } =
    useMatch(matchId);
  const [rematchUi, dispatchRematch] = useReducer(rematchUiReducer, IDLE_REMATCH);

  // 已經導向過的新對局 id。兩條通知路徑共用它，所以同一場再戰只會導一次。
  const navigatedToRef = useRef<string | null>(null);

  const goToRematch = useCallback(
    (target: string | null) => {
      if (!shouldNavigateToRematch(matchId, target, navigatedToRef.current)) return;
      navigatedToRef.current = target;
      navigate(`/m/${encodeURIComponent(target as string)}`);
    },
    [matchId, navigate],
  );

  /*
   * ★ 先按再戰的人**只有這條路**能得知新對局存在（C02 §5 v1.2 的 `rematch_ready`）。
   *   後按的人會同時走這裡與下面的 HTTP 回應 —— `goToRematch` 的去重讓它只導一次。
   */
  useEffect(() => {
    goToRematch(rematchMatchId);
  }, [rematchMatchId, goToRematch]);

  /*
   * ★ 送出一次 `POST /rematch`（按下或輪詢都走這裡）。
   *
   *   `matched`              → 新對局已經開好，導頁。
   *   `waiting_for_opponent` → 不做事；等待狀態自己會繼續數，直到成局或到期。
   *   失敗                    → 見下方對 `origin` 的區分。
   */
  const sendRematch = useCallback(
    async (origin: 'press' | 'poll') => {
      try {
        const res = await rematch();
        if (res.status === 'matched') {
          dispatchRematch({ type: 'MATCHED' });
          goToRematch(res.match_id);
          return;
        }
        /*
         * ★ R-5 的錨點就是這一行。
         *   `waiting_for_opponent` 代表伺服器剛剛執行了 `SET key_mine EX 120`，
         *   所以**這一刻**（收到回應的時刻）才是「再活 120 秒」的起算點。
         *   前端不得另外算一個放棄時刻 —— 那個數字沒有任何東西保證它跟這個相等。
         */
        dispatchRematch({ type: 'INTENT_RENEWED', now: Date.now() });
      } catch (e: unknown) {
        const code = rematchErrorCode(e);
        /*
         * ★ 玩家**按下**時的失敗一律要說出來（缺陷 4：409 被靜靜吞掉，
         *   玩家以為自己點歪了而反覆按）；但**輪詢**時的暫時性失敗不得中止等待 ——
         *   輪詢是補救路徑，一次網路抖動就把出路關掉，等於把補救變成新的死路。
         *
         * ★ 兩條路都不會丟掉監護責任：`FAILED` 在 reducer 裡會保留（必要時延長）
         *   `intentExpiresAt`，因為「這次呼叫失敗」不代表「伺服器上沒有我的意願」。
         */
        if (origin === 'poll' && isTransientRematchFailure(code)) {
          if (mayHaveArmedIntent(code)) dispatchRematch({ type: 'INTENT_RENEWED', now: Date.now() });
          return;
        }
        dispatchRematch({
          type: 'FAILED',
          code,
          message: e instanceof Error ? e.message : String(e),
          now: Date.now(),
        });
      }
    },
    [goToRematch, rematch],
  );

  /*
   * ★ 尾窗的**唯讀**守望：`GET /lobby/{game_key}` → `my_active_match`。
   *
   *   為什麼不是再打一次 `POST /rematch`：那會 `SET key_mine EX 120`，
   *   鑰匙永遠不死，後端 TTL 的設計意圖（不要把很久以前按過的人拉進新對局）
   *   就被前端單方面廢掉了。這條 GET 完全不碰任何 redis key，
   *   所以「盲窗為零」與「邀請仍會如期死亡」可以同時成立。
   */
  const watchForRematchMatch = useCallback(async () => {
    try {
      const found = await findMyActiveMatch();
      if (found) goToRematch(found);
    } catch {
      /* 備援的備援，失敗就等下一個週期；真正的通道是 `rematch_ready` 事件 */
    }
  }, [findMyActiveMatch, goToRematch]);

  const onRematch = useCallback(() => {
    dispatchRematch({ type: 'PRESSED', now: Date.now() });
    void sendRematch('press');
  }, [sendRematch]);

  /*
   * ★ 等待期間的心跳。三種醒來各有各的事做，而且**三件事互不相等**：
   *     * 續期輪詢（`POST /rematch`）—— 只在續期預算內；
   *     * 唯讀守望（`GET /lobby`）—— 停止續期之後、鑰匙死亡之前；
   *     * 宣布過期 —— 只在 `intentExpiresAt` 那一刻。
   *   第二輪把後兩件事併成同一個時刻，就是 R-5 的盲窗。
   *
   *   `now` 取 `Math.max(Date.now(), wakeAt)` 是**必要的**，不是保險：
   *   計時器可能比排定時刻早觸發 1 毫秒，那時 `rematchUiReducer` 會回傳**同一個物件**
   *   （referential equality）→ React 略過重繪 → 這個 effect 不重跑 → 計時器就此停擺，
   *   等待又變回沒有盡頭的狀態。以排定時刻為準可以讓狀態一定前進。
   *   代價是「現在幾點」最多說謊 1 毫秒，對 120 秒的期限毫無影響。
   */
  useEffect(() => {
    const delay = nextRematchWakeupMs(rematchUi, Date.now());
    if (delay === null) return;
    const wakeAt = Date.now() + delay;
    const timer = setTimeout(() => {
      const now = Math.max(Date.now(), wakeAt);
      const poll = shouldPollNow(rematchUi, now);
      const watch = shouldWatchNow(rematchUi, now);
      dispatchRematch({ type: 'TICK', now });
      if (poll) void sendRematch('poll');
      else if (watch) void watchForRematchMatch();
    }, delay);
    return () => clearTimeout(timer);
  }, [rematchUi, sendRematch, watchForRematchMatch]);

  const onForfeit = useCallback(async () => {
    if (!globalThis.confirm?.(t('match.forfeitConfirm'))) return;
    try {
      await forfeit();
    } catch {
      /* 後端會用事件告訴我們結果 */
    }
  }, [forfeit, t]);

  if (loadError) {
    return (
      <div className="notice notice--error stack stack--tight">
        <strong>{loadError === 'MATCH_NOT_FOUND' ? t('error.notFound') : t('error.generic')}</strong>
        <Link to="/">{t('match.backToLobby')}</Link>
      </div>
    );
  }

  const opp = opponentSeat(state);
  const ended = state.phase === 'ended' || state.phase === 'voided';
  const affordance = rematchAffordance(state.status, rematchUi);
  const notice = rematchNotice(rematchUi);
  const endedHint = endedHintKey(state.status, matchResultFor(state));
  const announcement = announcementText(state);

  return (
    <div className="match">
      <ReconnectBanner connection={state.connection} attempt={state.reconnectAttempt} />

      {/*
        ★ 管理員的對局內公告（需求 1.2.3）。公告**內文**是管理員自己打的字，
          不進字典（翻不了）；只有標籤是文案。放在最上面是因為它多半是
          「這一局有狀況，請稍候」這一類必須先看到的話。
      */}
      {announcement ? (
        <div className="notice notice--warn stack stack--tight" role="status" aria-live="polite">
          <strong>{t('match.announcement')}</strong>
          <span>{announcement}</span>
        </div>
      ) : null}

      <ScoreBoard state={state} />

      <div className="spread">
        <span className="badge">{t('match.round', { n: Math.max(1, state.roundNo) })}</span>
        <span className="faint">{state.variantKey ?? ''}</span>
      </div>

      {/* --- 主要區塊：依 phase 切換 --- */}
      {state.phase === 'connecting' ? <p className="muted">{t('common.loading')}</p> : null}

      {state.phase === 'waiting_opponent' ? (
        <section className="waiting" aria-live="polite">
          <div className="waiting__dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <p className="h3">{t('match.waitingStart')}</p>
        </section>
      ) : null}

      {state.phase === 'choosing' || state.phase === 'submitting' ? (
        <section className="stack">
          <div className="spread">
            <h2 className="h2">{t('match.chooseNow')}</h2>
            <Countdown remainingMs={remainingMs} progress={progress} />
          </div>
          <ChoiceButtons
            legalActions={state.legalActions.length > 0 ? state.legalActions : ['rock', 'paper', 'scissors']}
            disabled={!canSubmit(state)}
            selected={state.myPendingAction}
            onPick={submit}
          />
          {state.deadlinePassed ? <p className="notice notice--warn">{t('match.deadlinePassed')}</p> : null}
          <p className="faint">{t('match.timeoutPolicy')}</p>
        </section>
      ) : null}

      {state.phase === 'submitted' ? <WaitingForOpponent myAction={state.myPendingAction} /> : null}

      {state.phase === 'revealing' || state.phase === 'round_summary' ? <RevealPanel state={state} /> : null}

      {state.phase === 'voided' ? <div className="notice notice--warn">{t('match.voided')}</div> : null}

      {state.phase === 'ended' ? (
        <section className="card stack">
          {/*
            ★ 標題先看 `status` 再看勝負（見 `endedHeadlineKey`）：
              * `abandoned` → 「對手沒有出現」，而不是「結果未知」；
              * 其餘四路（win / lose / draw / unknown）由 `Record<MatchResult, StringKey>`
                強迫寫齊 —— 「讀不到勝者」**不可能**再安靜地退化成「和局」（C02 §6.1 v1.2）。
          */}
          <h2 className="h1">{t(endedHeadlineKey(state.status, matchResultFor(state)))}</h2>
          {endedHint ? <p className="muted">{t(endedHint)}</p> : null}
          <div className="row">
            {affordance.kind === 'button' ? (
              <button
                type="button"
                className="btn btn--primary"
                onClick={onRematch}
                disabled={affordance.disabled}
                aria-busy={affordance.disabled}
              >
                {t(affordance.labelKey)}
              </button>
            ) : null}
            {matchId ? (
              <Link className="btn btn--sm" to={`/r/${encodeURIComponent(matchId)}`}>
                {t('match.replay')}
              </Link>
            ) : null}
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate('/')}>
              {t('match.backToLobby')}
            </button>
          </div>
          {/*
            ★ 每一種「等不到」都要有一句話。
              * 沒有按鈕（abandoned / voided）→ 說明為什麼這裡不能再戰；
              * 等到 TTL 還是沒人回應 → 「對手還沒回應」＋ 按鈕已經回復可按；
              * 伺服器明確拒絕（例如 409）→ 把原因說出來，而不是讓按鈕默默彈回去。
          */}
          {affordance.kind === 'unavailable' ? <p className="faint">{t(affordance.reasonKey)}</p> : null}
          {notice ? (
            <p className={notice.tone === 'error' ? 'notice notice--error' : 'notice notice--warn'} aria-live="polite">
              {t(notice.key)}
            </p>
          ) : null}
        </section>
      ) : null}

      {/*
        ★ R-6：橫幅只出現字典裡的句子。`code` / `message` 是給開發者的，
          由 `useMatch` 記進 console（reducer 保持純函式）。
          在此之前，C02 §6.2 的期限退化會讓玩家看到
          `round_started: deadline_at absent — derived from …` 這串英文。
      */}
      {state.error ? (
        <div className="notice notice--error spread" role="alert">
          <span>{t(errorNoticeKey(state.error.code))}</span>
        </div>
      ) : null}

      {/* --- 對戰紀錄（只含已結算回合） --- */}
      <section className="card stack stack--tight">
        <h2 className="h2">{t('match.history')}</h2>
        <HistoryTable history={state.history} mySeat={state.mySeat} opponentSeatNo={opp?.seat ?? null} />
      </section>

      {/* --- ★ 教學重點：可驗證公平，全程可見（但種子只在 completed 之後才給） --- */}
      <FairnessPanel
        matchId={state.matchId}
        fairness={fairnessForDisplay(state.fairness, state.status)}
        rounds={state.history}
        ended={ended}
      />

      {!ended ? (
        <div className="row">
          <button type="button" className="btn btn--danger btn--sm" onClick={() => void onForfeit()}>
            {t('match.forfeit')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
